import { FC, useEffect } from 'react'
import { Controller, RegisterOptions, useForm } from 'react-hook-form'

import {
	Alert,
	Container,
	FormField,
	Header,
	Input,
	Link,
	RadioGroup,
	Select,
	SpaceBetween
} from '@cloudscape-design/components'

import {
	DEFAULT_SANDBOX_CRON_DESCRIPTION,
	DEFAULT_SANDBOX_CRON_SYNTAX,
	DEFAULT_SANDBOX_SCHEDULE_DAY,
	DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH,
	DEFAULT_SANDBOX_SCHEDULE_FREQUENCY,
	DEFAULT_SANDBOX_SCHEDULE_MONTH,
	DEFAULT_SANDBOX_SCHEDULE_TIME,
	SANDBOX_CRON_TIMEZONE,
	SANDBOX_DAY_OF_MONTH_OPTIONS,
	SANDBOX_FREQUENCY_OPTIONS,
	SANDBOX_MONTH_OPTIONS,
	SANDBOX_SCHEDULE_MODE,
	SANDBOX_TIME_OPTIONS,
	SANDBOX_WEEKDAY_OPTIONS,
	describeSandboxSchedule,
	getSandboxCronSyntaxError,
	getSandboxScheduleMode
} from '../../../../helper/gcpProjectSandboxSchedule'
import { usegcpProjectCreationFormStore } from '../../../../stores/gcpProjects/newgcpProjectCreationFormStore'
import { StepConfig } from '../steps_config'

const getSelectedProjectType = (gcpProjectType: unknown): string => {
	if (typeof gcpProjectType === 'string') {
		return gcpProjectType
	}

	if (
		gcpProjectType &&
		typeof gcpProjectType === 'object' &&
		'value' in gcpProjectType
	) {
		return String((gcpProjectType as { value?: string }).value ?? '')
	}

	return ''
}

const getDaysInSandboxScheduleMonth = (month: string): number => {
	if (month === '2') {
		return 28
	}

	if (['4', '6', '9', '11'].includes(month)) {
		return 30
	}

	return 31
}

const step2Fields: Array<StepConfig> = [
	{
		label: 'Project Viewer Distribution List',
		fieldName: 'ProjectViewerDistributionList',
		description:
			'The Project Viewer Distribution List serves as the GCP Project email. The standard format is: gcp-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph',
		info: (
			<Link
				external
				href="https://compass.globe.com.ph/latest/gcp/user-guide/asp/meet_criteria/"
			>
				Valid Distribution List Link
			</Link>
		),
		type: 'text',
		placeholder:
			'gcp-glo02-<group_name>-<platform_name>-<environment>@globe.com.ph'
	},
	{
		label: 'Budget Alerts Distribution List',
		fieldName: 'BudgetAlertsDistributionList',
		description:
			'The Budget Alerts Distribution List receives budget-related alerts. The standard format is: gcp-glo02-<group_name>-<platform_name>-budgetowners@globe.com.ph',
		type: 'text',
		placeholder:
			'gcp-glo02-<group_name>-<platform_name>-budgetowners@globe.com.ph'
	},
	{
		label: 'Platform Owner Email',
		fieldName: 'PlatformOwnerEmail',
		description:
			'The Platform Owner Email notifies the Platform Owner during approval.',
		type: 'email',
		placeholder: 'juan.cruz@globe.com.ph'
	},
	{
		label: 'Budget Threshold Alert',
		fieldName: 'MonthlyBudgetCap',
		description:
			'Please set the initial monthly budget cap for each GCP project during project creation. The service owner can update the budget cap later via the pipeline. Ensure the budget cap here matches the approved budget for your project or workload.',
		type: 'number',
		constraintText: 'Value should be in USD. Example: 100 (means 100USD)'
	}
]

const getFieldTypeRules = (
	fieldType: string,
	fieldName: string
): RegisterOptions => {
	switch (fieldType) {
		case 'text':
			if (fieldName === 'ProjectViewerDistributionList') {
				return {
					required: 'Project Viewer Distribution List is required',
					pattern: {
						value:
							/^gcp-glo02-[a-zA-Z]+-[a-zA-Z0-9]+-[a-zA-Z]+@globe\.com\.ph$/,
						message: 'Invalid Project Viewer Distribution List email address'
					}
				}
			}

			if (fieldName === 'BudgetAlertsDistributionList') {
				return {
					required: 'Budget Alerts Distribution List is required',
					pattern: {
						value:
							/^gcp-glo02-[a-zA-Z]+-[a-zA-Z0-9]+-budgetowners@globe\.com\.ph$/,
						message: 'Invalid Budget Alerts Distribution List email address'
					}
				}
			}

			if (fieldName === 'CronSyntax') {
				return {}
			}

			return { required: true }
		case 'number':
			return {
				required: 'Budget Threshold Alert is required',
				min: {
					value: 0,
					message: 'Value should be greater than 0'
				},
				max: {
					value: 100000,
					message: 'Value should be less than 100000'
				}
			}
		case 'email':
			return {
				required: 'Platform Owner Email is required',
				pattern: {
					value: /^[^\s@]+@globe\.com\.ph$/,
					message: 'Invalid Project Owner Email Address.'
				}
			}
		case 'disabledText':
			return {}
		default:
			return { required: true }
	}
}

const Step2: FC = () => {
	const {
		formValues,
		actions: { setFormValues, setFormValidity }
	} = usegcpProjectCreationFormStore()

	const {
		control,
		clearErrors,
		formState: { isValid },
		setValue
	} = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	const selectedProjectType = getSelectedProjectType(formValues.gcpProjectType)
	const infrastructureType = formValues.infrastructureType || 'GKE'
	const isSandboxProject = selectedProjectType === 'Sandbox'
	const sandboxCronTimezoneFieldName = `${selectedProjectType}CronTimezone`
	const sandboxCronTimezoneValue = formValues[sandboxCronTimezoneFieldName]
	const sandboxScheduleModeFieldName = `${selectedProjectType}ScheduleMode`
	const sandboxCronFrequencyFieldName = `${selectedProjectType}CronFrequency`
	const sandboxCronDayFieldName = `${selectedProjectType}CronDayOfWeek`
	const sandboxCronDayOfMonthFieldName = `${selectedProjectType}CronDayOfMonth`
	const sandboxCronMonthFieldName = `${selectedProjectType}CronMonth`
	const sandboxCronTimeFieldName = `${selectedProjectType}CronTime`
	const sandboxCronSyntaxFieldName = `${selectedProjectType}CronSyntax`
	const sandboxScheduleMode = getSandboxScheduleMode(
		formValues[sandboxScheduleModeFieldName]
	)
	const sandboxCronDay = String(
		formValues[sandboxCronDayFieldName] ?? DEFAULT_SANDBOX_SCHEDULE_DAY
	)
	const sandboxCronFrequency = String(
		formValues[sandboxCronFrequencyFieldName] ??
			DEFAULT_SANDBOX_SCHEDULE_FREQUENCY
	)
	const sandboxCronDayOfMonth = String(
		formValues[sandboxCronDayOfMonthFieldName] ??
			DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH
	)
	const sandboxCronMonth = String(
		formValues[sandboxCronMonthFieldName] ?? DEFAULT_SANDBOX_SCHEDULE_MONTH
	)
	const sandboxCronTime = String(
		formValues[sandboxCronTimeFieldName] ?? DEFAULT_SANDBOX_SCHEDULE_TIME
	)
	const maximumSandboxDayOfMonth =
		sandboxCronFrequency === 'monthly'
			? 28
			: sandboxCronFrequency === 'yearly'
			? getDaysInSandboxScheduleMonth(sandboxCronMonth)
			: 31
	const sandboxDayOfMonthOptions = SANDBOX_DAY_OF_MONTH_OPTIONS.filter(
		(option) => Number(option.value) <= maximumSandboxDayOfMonth
	)
	const sandboxCronSyntaxError =
		sandboxScheduleMode === SANDBOX_SCHEDULE_MODE.ADVANCED
			? getSandboxCronSyntaxError(formValues[sandboxCronSyntaxFieldName])
			: undefined

	const updateSandboxScheduleMode = (mode: string) => {
		setFormValues(sandboxScheduleModeFieldName, mode)
		if (mode !== SANDBOX_SCHEDULE_MODE.ADVANCED) {
			clearErrors(sandboxCronSyntaxFieldName)
		}

		if (mode !== SANDBOX_SCHEDULE_MODE.DEFAULT) {
			return
		}

		setFormValues(sandboxCronFrequencyFieldName, '')
		setFormValues(sandboxCronDayFieldName, '')
		setFormValues(sandboxCronDayOfMonthFieldName, '')
		setFormValues(sandboxCronMonthFieldName, '')
		setFormValues(sandboxCronTimeFieldName, '')
		setFormValues(sandboxCronSyntaxFieldName, '')
	}

	useEffect(() => {
		if (isSandboxProject) {
			return
		}

		if (!formValues.infrastructureType) {
			setFormValues('infrastructureType', 'GKE')
			setFormValues('isGKE', true)
		}
	}, [formValues.infrastructureType, isSandboxProject, setFormValues])

	useEffect(() => {
		setFormValidity(isValid && !sandboxCronSyntaxError)
	}, [isValid, sandboxCronSyntaxError, setFormValidity])

	useEffect(() => {
		if (!isSandboxProject) {
			return
		}

		setValue(sandboxCronTimezoneFieldName, SANDBOX_CRON_TIMEZONE, {
			shouldValidate: true,
			shouldDirty: false
		})

		if (sandboxCronTimezoneValue !== SANDBOX_CRON_TIMEZONE) {
			setFormValues(sandboxCronTimezoneFieldName, SANDBOX_CRON_TIMEZONE)
		}

		if (!formValues[sandboxScheduleModeFieldName]) {
			setFormValues(sandboxScheduleModeFieldName, SANDBOX_SCHEDULE_MODE.DEFAULT)
		}
	}, [
		isSandboxProject,
		sandboxCronTimezoneFieldName,
		sandboxCronTimezoneValue,
		sandboxScheduleModeFieldName,
		formValues,
		setFormValues,
		setValue
	])

	useEffect(() => {
		if (
			sandboxCronFrequency !== 'monthly' &&
			sandboxCronFrequency !== 'yearly'
		) {
			return
		}

		if (Number(sandboxCronDayOfMonth) > maximumSandboxDayOfMonth) {
			setFormValues(
				sandboxCronDayOfMonthFieldName,
				String(maximumSandboxDayOfMonth)
			)
		}
	}, [
		sandboxCronDayOfMonth,
		sandboxCronDayOfMonthFieldName,
		sandboxCronFrequency,
		maximumSandboxDayOfMonth,
		setFormValues
	])

	return (
		<SpaceBetween direction="vertical" size="l">
			{!isSandboxProject && (
				<Container header={<Header variant="h3">Infrastructure Type</Header>}>
					<FormField>
						<RadioGroup
							value={infrastructureType}
							items={[
								{ value: 'GKE', label: 'GKE' },
								{ value: 'Non-GKE', label: 'Non-GKE' }
							]}
							onChange={({ detail }) => {
								const nextIsGke = detail.value === 'GKE'
								setFormValues('infrastructureType', detail.value)
								setFormValues('isGKE', nextIsGke)
							}}
						/>
					</FormField>
				</Container>
			)}
			<Container>
				<Header variant="h3">{selectedProjectType} Project Details</Header>
				<SpaceBetween direction="vertical" size="l">
					{step2Fields.map((field: StepConfig, i) => {
						const gcpProjectTypeFieldName = `${selectedProjectType}${field.fieldName}`

						return (
							<Controller
								name={gcpProjectTypeFieldName}
								control={control}
								key={i}
								rules={getFieldTypeRules(field.type, field.fieldName)}
								render={({
									field: { name, onChange, value },
									fieldState: { error }
								}) => (
									<FormField
										label={field.label}
										description={field.description}
										info={field.info}
										constraintText={field.constraintText}
										errorText={error?.message}
									>
										{(field.type === 'text' || field.type === 'email') && (
											<Input
												placeholder={field.placeholder}
												name={name}
												value={value ?? ''}
												onChange={({ detail }) => {
													onChange(detail.value)
													setFormValues(gcpProjectTypeFieldName, detail.value)
												}}
											/>
										)}

										{field.type === 'number' && (
											<Input
												name={name}
												value={String(value ?? '')}
												type="number"
												onChange={({ detail }) => {
													onChange(detail.value)
													setFormValues(
														gcpProjectTypeFieldName,
														detail.value === '' ? '' : +detail.value
													)
												}}
											/>
										)}
									</FormField>
								)}
							/>
						)
					})}
				</SpaceBetween>
			</Container>
			{isSandboxProject && (
				<Container
					header={
						<Header
							variant="h3"
							description={
								<>
									As part of cost optimization initiatives, each GCP Sandbox
									project will map to its own dedicated Nuker Pipeline
									repository. The pipeline will execute gcp-nuke on a
									pre-configured schedule to purge non-whitelisted resources.
								</>
							}
						>
							{selectedProjectType} Resource Nuker Pipeline Details
						</Header>
					}
				>
					<SpaceBetween direction="vertical" size="l">
						<Alert statusIconAriaLabel="Info">
							The equivalent Resource Nuker Pipeline will be created under the{' '}
							<Link
								external
								href="https://gitlab.com/globetelecom/infrastructure/gcp/landing-zone/resources-nuker"
							>
								resources-nuker subgroup
							</Link>
							. <br />
							For more information on what the Resource Nuker Pipeline is, see
							the{' '}
							<Link
								external
								href="https://globetelecom.atlassian.net/wiki/x/LoAVrAE"
							>
								GCP Sandbox Resources Nuker Implementation
							</Link>
							.
						</Alert>
						<FormField
							label="Resource cleanup schedule"
							description="Use the standard cleanup schedule, or configure when the Resource Nuker Pipeline runs. All schedules use Singapore time (UTC+8)."
							stretch
						>
							<RadioGroup
								value={sandboxScheduleMode}
								items={[
									{
										value: SANDBOX_SCHEDULE_MODE.DEFAULT,
										label: `Use default schedule — ${DEFAULT_SANDBOX_CRON_DESCRIPTION}`
									},
									{
										value: SANDBOX_SCHEDULE_MODE.CUSTOM,
										label: 'Create a custom schedule'
									},
									{
										value: SANDBOX_SCHEDULE_MODE.ADVANCED,
										label: 'Enter a custom cron expression'
									}
								]}
								onChange={({ detail }) =>
									updateSandboxScheduleMode(detail.value)
								}
							/>
						</FormField>

						{sandboxScheduleMode === SANDBOX_SCHEDULE_MODE.CUSTOM && (
							<SpaceBetween direction="vertical" size="m">
								<FormField label="Frequency">
									<Select
										selectedOption={
											SANDBOX_FREQUENCY_OPTIONS.find(
												(option) => option.value === sandboxCronFrequency
											) ?? null
										}
										options={SANDBOX_FREQUENCY_OPTIONS}
										onChange={({ detail }) =>
											setFormValues(
												sandboxCronFrequencyFieldName,
												detail.selectedOption.value ??
													DEFAULT_SANDBOX_SCHEDULE_FREQUENCY
											)
										}
									/>
								</FormField>
								{sandboxCronFrequency === 'weekly' && (
									<FormField label="Day of week">
										<Select
											selectedOption={
												SANDBOX_WEEKDAY_OPTIONS.find(
													(option) => option.value === sandboxCronDay
												) ?? null
											}
											options={SANDBOX_WEEKDAY_OPTIONS}
											onChange={({ detail }) =>
												setFormValues(
													sandboxCronDayFieldName,
													detail.selectedOption.value ??
														DEFAULT_SANDBOX_SCHEDULE_DAY
												)
											}
										/>
									</FormField>
								)}
								{sandboxCronFrequency === 'yearly' && (
									<FormField label="Month">
										<Select
											selectedOption={
												SANDBOX_MONTH_OPTIONS.find(
													(option) => option.value === sandboxCronMonth
												) ?? null
											}
											options={SANDBOX_MONTH_OPTIONS}
											onChange={({ detail }) => {
												const nextMonth =
													detail.selectedOption.value ??
													DEFAULT_SANDBOX_SCHEDULE_MONTH
												const maximumDayOfMonth =
													getDaysInSandboxScheduleMonth(nextMonth)

												setFormValues(sandboxCronMonthFieldName, nextMonth)
												if (Number(sandboxCronDayOfMonth) > maximumDayOfMonth) {
													setFormValues(
														sandboxCronDayOfMonthFieldName,
														String(maximumDayOfMonth)
													)
												}
											}}
										/>
									</FormField>
								)}
								{(sandboxCronFrequency === 'monthly' ||
									sandboxCronFrequency === 'yearly') && (
									<FormField
										label="Day of month"
										constraintText={
											sandboxCronFrequency === 'monthly'
												? 'Monthly schedules are limited to days 1–28 to ensure they run every month.'
												: undefined
										}
									>
										<Select
											selectedOption={
												sandboxDayOfMonthOptions.find(
													(option) => option.value === sandboxCronDayOfMonth
												) ?? null
											}
											options={sandboxDayOfMonthOptions}
											onChange={({ detail }) =>
												setFormValues(
													sandboxCronDayOfMonthFieldName,
													detail.selectedOption.value ??
														DEFAULT_SANDBOX_SCHEDULE_DAY_OF_MONTH
												)
											}
										/>
									</FormField>
								)}
								<FormField label="Time" constraintText="Singapore time (UTC+8)">
									<Select
										selectedOption={
											SANDBOX_TIME_OPTIONS.find(
												(option) => option.value === sandboxCronTime
											) ?? null
										}
										options={SANDBOX_TIME_OPTIONS}
										onChange={({ detail }) =>
											setFormValues(
												sandboxCronTimeFieldName,
												detail.selectedOption.value ??
													DEFAULT_SANDBOX_SCHEDULE_TIME
											)
										}
									/>
								</FormField>
								<Alert statusIconAriaLabel="Info">
									{describeSandboxSchedule({
										mode: sandboxScheduleMode,
										frequency: sandboxCronFrequency,
										day: sandboxCronDay,
										dayOfMonth: sandboxCronDayOfMonth,
										month: sandboxCronMonth,
										time: sandboxCronTime,
										cronSyntax: formValues[sandboxCronSyntaxFieldName]
									})}
								</Alert>
							</SpaceBetween>
						)}

						{sandboxScheduleMode === SANDBOX_SCHEDULE_MODE.ADVANCED && (
							<Controller
								name={sandboxCronSyntaxFieldName}
								control={control}
								rules={{
									validate: (value) => getSandboxCronSyntaxError(value) ?? true
								}}
								render={({
									field: { onChange, value },
									fieldState: { error }
								}) => (
									<FormField
										label="Cron expression"
										description={
											<>
												Use GitLab's five-field Cron format.{' '}
												<Link
													external
													href="https://docs.gitlab.com/topics/cron/"
												>
													What is Cron syntax?
												</Link>
											</>
										}
										constraintText={
											<>
												Default: {DEFAULT_SANDBOX_CRON_SYNTAX}
												<br />
												Example: 0 23 * * 4 (Every Thursday at 11 PM)
											</>
										}
										errorText={error?.message ?? sandboxCronSyntaxError}
									>
										<Input
											placeholder={DEFAULT_SANDBOX_CRON_SYNTAX}
											value={String(value ?? '')}
											onChange={({ detail }) => {
												onChange(detail.value)
												setFormValues(sandboxCronSyntaxFieldName, detail.value)
											}}
										/>
									</FormField>
								)}
							/>
						)}
						{sandboxScheduleMode === SANDBOX_SCHEDULE_MODE.ADVANCED && (
							<Alert type="warning" statusIconAriaLabel="Warning">
								Advanced cron expressions may use dates such as the 31st or
								February 29.
								<br />
								GitLab skips the scheduled pipeline when the selected date does
								not occur.
							</Alert>
						)}

						<FormField label="Cron timezone">
							<Input value={SANDBOX_CRON_TIMEZONE} disabled />
						</FormField>
					</SpaceBetween>
				</Container>
			)}
		</SpaceBetween>
	)
}

export default Step2
